import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { insertLivePic, updateLivePic } from '../services';
import { useS3Uploader } from './useS3Uploader';
import { UserData, useToaster, useUserContext } from '../context';
import imageCompression from 'browser-image-compression';

export interface ProfileData {
  imageContent: string;
  practiseUserId: string;
  status: string;
  type?: string;
}

const fetchProfileDetails = async (
  user: UserData | null,
  generateSignedUrl: (key: string) => Promise<string>
): Promise<UserData> => {
  if (!user) {
    throw new Error('User not found');
  }

  if (!user.image) {
    return user;
  }

  const signedUrl = await generateSignedUrl(user.image);

  return {
    ...user,
    image: signedUrl
  };
};

export const useProfile = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToaster } = useToaster();

  const { user, setUser } = useUserContext();
  const [fileName, setFileName] = useState<string | null>(null);

  const { getModifiedFileName, uploadFileFromUri, generateSignedUrl } = useS3Uploader();

  const goBack = useCallback(() => window.history.back(), []);

  /**
   * -------------------------
   * FETCH PROFILE (QUERY)
   * -------------------------
   */
  const {
    data: details,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['profile', user?.userId],
    queryFn: () => fetchProfileDetails(user, generateSignedUrl),
    enabled: !!user?.userId,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData
  });

  /**
   * -------------------------
   * UPLOAD / UPDATE PROFILE PIC (MUTATION)
   * -------------------------
   */
  const uploadProfileMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.userId) {
        throw new Error('User ID missing');
      }

      const isUpdate = Boolean(user.image);
      const modifiedName = getModifiedFileName(file.name, user.userId);

      const key = await uploadFileFromUri(file, modifiedName);
      if (!key) {
        throw new Error('File upload failed');
      }

      const payload: ProfileData = {
        imageContent: key,
        practiseUserId: user.userId,
        status: 'unverified',
        ...(isUpdate ? {} : { type: user.userRole })
      };

      isUpdate ? await updateLivePic(payload) : await insertLivePic(payload);

      const signedUrl = await generateSignedUrl(key);

      return { key, signedUrl };
    },
    onSuccess: ({ key, signedUrl }) => {
      // update user context
      if (user) {
        setUser({
          ...user,
          image: key
        });
      }

      // update query cache instantly (no refetch delay)
      queryClient.setQueryData(['profile', user?.userId], (old: UserData | undefined) =>
        old
          ? {
              ...old,
              image: signedUrl
            }
          : old
      );
    }
  });

  /**
   * -------------------------
   * FILE CHANGE HANDLER
   * -------------------------
   */
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
      const fileFromInput = event.target.files?.[0];
      if (!fileFromInput || !user?.userId) return false;

      let file: File = fileFromInput;

      /**
       * -------------------------
       * 1 EXTENSION & MIME CHECK
       * -------------------------
       */
      const allowedExtensions = ['png', 'jpg', 'jpeg'];
      const allowedMimeTypes = ['image/png', 'image/jpg', 'image/jpeg'];

      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

      if (!allowedExtensions.includes(fileExtension) || !allowedMimeTypes.includes(file.type)) {
        showToaster('info', t('files.format'));
        return false;
      }

      /**
       * -------------------------
       * 2 FILE SIZE CHECK (2MB)
       * -------------------------
       */
      if (file.size > 2 * 1024 * 1024) {
        showToaster('info', t('files.size'));
        return false;
      }

      /**
       * -------------------------
       * 3 IMAGE DIMENSION CHECK
       * -------------------------
       */
      const isValidDimensions = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 400 || img.height > 400) {
            showToaster('info', t('files.dimensions'));
            resolve(false);
          } else {
            resolve(true);
          }
        };
        img.onerror = () => resolve(false);
        img.src = URL.createObjectURL(file);
      });

      if (!isValidDimensions) return false;

      /**
       * -------------------------
       * 4 IMAGE COMPRESSION
       * -------------------------
       */
      try {
        const fileSizeMB = file.size / (1024 * 1024);

        const options = {
          maxWidthOrHeight: 1400,
          useWebWorker: true,
          maxSizeMB:
            fileSizeMB < 0.5 ? fileSizeMB : fileSizeMB < 1.5 ? fileSizeMB * 0.7 : fileSizeMB * 0.5
        };

        const compressed = await imageCompression(file, options);
        file = new File([compressed], file.name, { type: compressed.type });
      } catch (err) {
        console.error('Compression error:', err);
        return false;
      }

      /**
       * -------------------------
       * 5 TANSTACK QUERY UPLOAD
       * -------------------------
       */
      try {
        await uploadProfileMutation.mutateAsync(file);
        return true;
      } catch (error) {
        console.error('Upload failed:', error);
        return false;
      }
    },
    [user?.userId, uploadProfileMutation, t]
  );

  const menuItems: string[] = [
    t('profile.organizationManagement'),
    t('profile.userSettings'),
    t('profile.reports'),
    t('profile.billing')
  ];

  return {
    goBack,
    details,
    isLoading,
    isError,
    handleFileChange,
    fileName,
    setFileName,
    menuItems,
    isUploading: uploadProfileMutation.isPending
  };
};
